import React, { useState, useEffect } from "react";
import { sectionToPath } from "../../routes/routeConfig";

const isModifiedEvent = (event) =>
  event.metaKey || event.altKey || event.ctrlKey || event.shiftKey;

const SidebarSectionLink = ({
  section,
  className,
  onNavigate,
  title,
  children,
}) => {
  const href = sectionToPath[section] || "/dashboard";

  return (
    <a
      href={href}
      className={className}
      title={title}
      onClick={(event) => {
        if (event.defaultPrevented) return;
        if (event.button !== 0 || isModifiedEvent(event)) return;
        event.preventDefault();
        onNavigate(section);
      }}
    >
      {children}
    </a>
  );
};

const Sidebar = ({
  collapsed,
  mobileOpen = false,
  onExpand,
  activeSection,
  setActiveSection,
  roleName,
  user,
}) => {
  const [openUserAccess, setOpenUserAccess] = useState(false);
  const [open3DUsers, setOpen3DUsers] = useState(false);
  const [openCompany, setOpenCompany] = useState(false);
  const [openActivities, setOpenActivities] = useState(false);
  const [openOrganization, setOpenOrganization] = useState(false);
  const [openAI, setOpenAI] = useState(false);
  const [openKPI, setOpenKPI] = useState(false);
  const [openSettings, setOpenSettings] = useState(false);
  const [openAttributes, setOpenAttributes] = useState(false);
  const [openHelp, setOpenHelp] = useState(false);

  // The top-level expandable groups behave as an accordion: opening one collapses
  // whichever other one was open, so only a single group's submenu is ever shown.
  const topLevelSetters = [
    setOpen3DUsers,
    setOpenUserAccess,
    setOpenCompany,
    setOpenActivities,
    setOpenOrganization,
    setOpenAI,
    setOpenHelp,
  ];

  // Toggle handler for an expandable group. When the rail is collapsed a group's
  // submenu can't be shown in place, so the first click expands the rail and opens
  // that group instead of toggling a hidden list (which would look like a dead click).
  const toggleGroup = (setOpen) => {
    // Collapse every other top-level group before (re)opening this one.
    topLevelSetters.forEach((s) => {
      if (s !== setOpen) s(false);
    });
    if (collapsed) {
      if (onExpand) onExpand();
      setOpen(true);
    } else {
      setOpen((v) => !v);
    }
  };

  // Keep submenu open when a related section is active
  useEffect(() => {
    if (
      [
        "user-access",
        "role-management",
        "audit-logs",
        "company-audit-logs",
        "asset-task-management",
        "kpi-management",
        "kpi-create",
        "kpi-edit",
        "uom-management",
        "kpi-attribute-management",
        "process-category-attributes",
        "process-status",
        "task-category-attributes",
        "task-status",
        "job-category-attributes",
        "job-status",
        "process-kpi",
        "countries",
        "country-create",
        "country-edit",
        "cities",
        "city-create",
        "city-edit",
      ].includes(activeSection)
    ) {
      setOpenUserAccess(true);
    }
    if (
      [
        "process-category-attributes",
        "process-status",
        "task-category-attributes",
        "task-status",
        "job-category-attributes",
        "job-status",
      ].includes(activeSection)
    ) {
      setOpenAttributes(true);
    }
    if (["users-3d-management"].includes(activeSection)) {
      setOpen3DUsers(true);
    }
    if (
      [
        "companies",
        "buildings",
        "building-create",
        "building-detail",
        "building-edit",
        "building-floors",
        "rooms",
        "company-management",
        "organization-management",
      ].includes(activeSection)
    ) {
      setOpenCompany(true);
    }
    if (
      [
        "process-management",
        "task-management",
        "bpmn-viewer",
        "process-usecase",
        "process-user-stories",
        "process-prototype",
      ].includes(activeSection)
    ) {
      setOpenActivities(true);
    }
    if (
      [
        "job-management",
        "job-workload",
        "function-management",
        "people-management",
        "functional-hierarchy",
        "unassigned-entities",
        // "c-level-library-new",
        "c-level-org-chart",
      ].includes(activeSection)
    ) {
      setOpenOrganization(true);
    }
    if (
      [
        "what-if-analysis",
        "ai-processes",
        "process-optimization",
        "process-optimization-copy",
        "substitution-analyst",
        "capi-paei-analysis",
        // "process-creation",
        // "process-visualization",
      ].includes(activeSection)
    ) {
      setOpenAI(true);
    }
    if (
      [
        "kpi-management",
        "kpi-create",
        "kpi-edit",
        "uom-management",
        "kpi-attribute-management",
      ].includes(activeSection)
    ) {
      setOpenKPI(true);
    }
    if (
      [
        "countries",
        "country-create",
        "country-edit",
        "cities",
        "city-create",
        "city-edit",
      ].includes(activeSection)
    ) {
      setOpenSettings(true);
    }
    if (["help-docs", "help-topic"].includes(activeSection)) {
      setOpenHelp(true);
    }
  }, [activeSection]);
  const icons = {
    dashboard: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z"
          fill="currentColor"
        />
      </svg>
    ),
    activities: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
      >
        <circle cx="6" cy="12" r="2" fill="currentColor" />
        <circle cx="12" cy="6" r="2" fill="currentColor" />
        <circle cx="18" cy="12" r="2" fill="currentColor" />
        <path
          d="M8 12h8M12 8v2"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    ),
    "process-management": (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M6 3h12v4H6V3zm0 7h12v4H6v-4zm0 7h12v4H6v-4z"
          fill="currentColor"
        />
      </svg>
    ),
    "task-management": (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M9 11l3 3L22 4l-2-2-8 8-3-3-2 2 5 5zM2 20h20v2H2v-2z"
          fill="currentColor"
        />
      </svg>
    ),
    organization: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M10 3h4v4h-4V3zm-6 6h4v4H4V9zm12 0h4v4h-4V9zM10 15h4v6h-4v-6zm-6 2h4v4H4v-4zm12 0h4v4h-4v-4z"
          fill="currentColor"
        />
      </svg>
    ),
    "job-management": (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M14 6V4H10v2H4v4h16V6h-6zM4 12v8h16v-8H4z"
          fill="currentColor"
        />
      </svg>
    ),
    "function-management": (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M12 2l3 7h7l-5.5 4.5L18 21l-6-4-6 4 1.5-7.5L2 9h7l3-7z"
          fill="currentColor"
        />
      </svg>
    ),
    // Parent icon for Structure section (match other SVG icons)
    company: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M4 20h16v-2H4v2zm2-4h12v-2H6v2zm-2-4h16V6H4v6zm2-4h12v2H6V8z"
          fill="currentColor"
        />
      </svg>
    ),
    "ai-processes": (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M12 2a5 5 0 015 5v1h1a4 4 0 110 8h-1v1a5 5 0 11-10 0v-1H6a4 4 0 110-8h1V7a5 5 0 015-5z"
          fill="currentColor"
        />
      </svg>
    ),
    "ai-agents": (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M12 2a5 5 0 015 5v1h1a4 4 0 110 8h-1v1a5 5 0 11-10 0v-1H6a4 4 0 110-8h1V7a5 5 0 015-5z"
          fill="currentColor"
        />
      </svg>
    ),
    "user-access": (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M12 12a5 5 0 100-10 5 5 0 000 10zm0 2c-5 0-9 2.5-9 5.5V22h18v-2.5C21 16.5 17 14 12 14z"
          fill="currentColor"
        />
      </svg>
    ),
    "3d-users": (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M12 2L2 7v10c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-10-5zm0 10a3 3 0 100-6 3 3 0 000 6zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
          fill="currentColor"
        />
      </svg>
    ),
    kpi: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M3 13h2v8H3v-8zm6-6h2v14H9V7zm6 3h2v11h-2V10zm6-7h2v18h-2V3z"
          fill="currentColor"
        />
      </svg>
    ),
    help: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M12 2a10 10 0 100 20 10 10 0 000-20zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26A1.99 1.99 0 0012 7a2 2 0 00-2 2H8a4 4 0 118 0c0 .88-.36 1.68-.93 2.25z"
          fill="currentColor"
        />
      </svg>
    ),
  };

  const menuItems = [
    { id: "dashboard", label: "Dashboard" },
    { id: "organization", label: "Organization" },
    { id: "activities", label: "Activities" },
    { id: "company", label: "Structure" },
    { id: "ai-agents", label: "AI Agents" },
    { id: "3d-users", label: "3D Users" },
    { id: "user-access", label: "Administration" },
    { id: "help", label: "Help & Docs" },
    // layout-management removed
  ];

  return (
    <aside
      className={`dashboard-sidebar ${collapsed ? "collapsed" : ""} ${mobileOpen ? "mobile-open" : ""}`}
      id="app-sidebar"
    >
      <nav className="sidebar-nav">
        <ul className="nav-list">
          {menuItems.map((item) => (
            <li key={item.id} className="nav-item">
              {item.id === "3d-users" ?
                (() => {
                  const roleText = String(
                    roleName ||
                      user?.roleName ||
                      user?.role_name ||
                      user?.role?.name ||
                      "",
                  )
                    .trim()
                    .toUpperCase();
                  if (roleText === "NORMAL_USER") return null;
                  return (
                    <>
                      <button
                        className={`nav-button ${
                          ["users-3d-management"].includes(activeSection) ?
                            "active"
                          : ""
                        }`}
                        onClick={() => toggleGroup(setOpen3DUsers)}
                        title={collapsed ? item.label : ""}
                      >
                        <span className="nav-icon" aria-hidden="true">
                          {icons[item.id]}
                        </span>
                        {!collapsed && (
                          <span
                            className="nav-label"
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              width: "100%",
                              alignItems: "center",
                            }}
                          >
                            {item.label}
                            <span
                              className={`submenu-caret ${
                                open3DUsers ? "open" : ""
                              }`}
                              aria-hidden="true"
                            >
                              ▾
                            </span>
                          </span>
                        )}
                      </button>
                      {!collapsed && open3DUsers && (
                        <ul
                          className="nav-sublist"
                          style={{ marginTop: 4, marginLeft: 36 }}
                        >
                          {roleText !== "NORMAL_USER" && (
                            <li className="nav-subitem">
                              <SidebarSectionLink
                                section="users-3d-management"
                                className={`nav-subbutton ${
                                  activeSection === "users-3d-management" ?
                                    "active"
                                  : ""
                                }`}
                                onNavigate={setActiveSection}
                              >
                                Manage 3D Users
                              </SidebarSectionLink>
                            </li>
                          )}
                        </ul>
                      )}
                    </>
                  );
                })()
              : item.id === "user-access" ?
                (() => {
                  const roleTextUpper = String(
                    roleName ||
                      user?.roleName ||
                      user?.role_name ||
                      user?.role?.name ||
                      "",
                  )
                    .trim()
                    .toUpperCase();
                  // Hide entire section for NORMAL_USER
                  if (roleTextUpper === "NORMAL_USER") return null;
                  return (
                    <>
                      <button
                        className={`nav-button ${
                          (
                            [
                              "user-access",
                              "role-management",
                              "audit-logs",
                              "company-audit-logs",
                              "kpi-management",
                              "kpi-create",
                              "kpi-edit",
                              "uom-management",
                              "kpi-attribute-management",
                              "process-kpi",
                            ].includes(activeSection)
                          ) ?
                            "active"
                          : ""
                        }`}
                        onClick={() => toggleGroup(setOpenUserAccess)}
                        title={collapsed ? item.label : ""}
                      >
                        <span className="nav-icon" aria-hidden="true">
                          {icons[item.id]}
                        </span>
                        {!collapsed && (
                          <span
                            className="nav-label"
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              width: "100%",
                              alignItems: "center",
                            }}
                          >
                            {item.label}
                            <span
                              className={`submenu-caret ${
                                openUserAccess ? "open" : ""
                              }`}
                              aria-hidden="true"
                            >
                              ▾
                            </span>
                          </span>
                        )}
                      </button>
                      {!collapsed && openUserAccess && (
                        <ul
                          className="nav-sublist"
                          style={{ marginTop: 4, marginLeft: 36 }}
                        >
                          <li className="nav-subitem">
                            <SidebarSectionLink
                              section="user-access"
                              className={`nav-subbutton ${
                                activeSection === "user-access" ? "active" : ""
                              }`}
                              onNavigate={setActiveSection}
                            >
                              User
                            </SidebarSectionLink>
                          </li>
                          {(() => {
                            const roleTextUpper2 = String(
                              roleName ||
                                user?.roleName ||
                                user?.role_name ||
                                user?.role?.name ||
                                "",
                            )
                              .trim()
                              .toUpperCase();
                            // Show Roles only for SUPER_ADMIN (matches the page and route guard)
                            return (
                              roleTextUpper2 === "SUPER_ADMIN" ||
                              roleTextUpper2 === "SUPERADMIN"
                            );
                          })() && (
                            <li className="nav-subitem">
                              <SidebarSectionLink
                                section="role-management"
                                className={`nav-subbutton ${
                                  activeSection === "role-management" ? "active"
                                  : ""
                                }`}
                                onNavigate={setActiveSection}
                              >
                                Roles
                              </SidebarSectionLink>
                            </li>
                          )}
                          {(() => {
                            const roleTextUpper3 = String(
                              roleName ||
                                user?.roleName ||
                                user?.role_name ||
                                user?.role?.name ||
                                "",
                            )
                              .trim()
                              .toUpperCase();
                            // Show Audit Logs only for SUPER_ADMIN
                            return (
                              roleTextUpper3 === "SUPER_ADMIN" ||
                              roleTextUpper3 === "SUPERADMIN"
                            );
                          })() && (
                            <>
                              <li className="nav-subitem">
                                <SidebarSectionLink
                                  section="audit-logs"
                                  className={`nav-subbutton ${
                                    activeSection === "audit-logs" ? "active"
                                    : ""
                                  }`}
                                  onNavigate={setActiveSection}
                                >
                                  Audit Logs
                                </SidebarSectionLink>
                              </li>
                              <li className="nav-subitem">
                                <SidebarSectionLink
                                  section="company-audit-logs"
                                  className={`nav-subbutton ${
                                    activeSection === "company-audit-logs" ?
                                      "active"
                                    : ""
                                  }`}
                                  onNavigate={setActiveSection}
                                >
                                  Company Logs
                                </SidebarSectionLink>
                              </li>
                              <li className="nav-subitem">
                                <SidebarSectionLink
                                  section="asset-task-management"
                                  className={`nav-subbutton ${
                                    activeSection === "asset-task-management" ?
                                      "active"
                                    : ""
                                  }`}
                                  onNavigate={setActiveSection}
                                >
                                  Asset Tasks
                                </SidebarSectionLink>
                              </li>
                              <li className="nav-subitem">
                                <button
                                  className={`nav-subbutton ${
                                    (
                                      [
                                        "kpi-management",
                                        "kpi-create",
                                        "kpi-edit",
                                        "uom-management",
                                        "kpi-attribute-management",
                                      ].includes(activeSection)
                                    ) ?
                                      "active"
                                    : ""
                                  }`}
                                  onClick={() => setOpenKPI((v) => !v)}
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    width: "100%",
                                  }}
                                >
                                  KPIs
                                  <span
                                    className={`submenu-caret ${openKPI ? "open" : ""}`}
                                    aria-hidden="true"
                                    style={{ marginLeft: 8 }}
                                  >
                                    ▾
                                  </span>
                                </button>
                                {openKPI && (
                                  <ul
                                    className="nav-sublist"
                                    style={{ marginTop: 4, marginLeft: 16 }}
                                  >
                                    <li className="nav-subitem">
                                      <SidebarSectionLink
                                        section="kpi-management"
                                        className={`nav-subbutton ${
                                          (
                                            [
                                              "kpi-management",
                                              "kpi-create",
                                              "kpi-edit",
                                            ].includes(activeSection)
                                          ) ?
                                            "active"
                                          : ""
                                        }`}
                                        onNavigate={setActiveSection}
                                      >
                                        KPI Management
                                      </SidebarSectionLink>
                                    </li>
                                    <li className="nav-subitem">
                                      <SidebarSectionLink
                                        section="uom-management"
                                        className={`nav-subbutton ${
                                          activeSection === "uom-management" ?
                                            "active"
                                          : ""
                                        }`}
                                        onNavigate={setActiveSection}
                                      >
                                        Units of Measure
                                      </SidebarSectionLink>
                                    </li>
                                    <li className="nav-subitem">
                                      <SidebarSectionLink
                                        section="kpi-attribute-management"
                                        className={`nav-subbutton ${
                                          (
                                            activeSection ===
                                            "kpi-attribute-management"
                                          ) ?
                                            "active"
                                          : ""
                                        }`}
                                        onNavigate={setActiveSection}
                                      >
                                        KPI Attributes
                                      </SidebarSectionLink>
                                    </li>
                                  </ul>
                                )}
                              </li>
                              <li className="nav-subitem">
                                <button
                                  className={`nav-subbutton ${
                                    (
                                      [
                                        "process-category-attributes",
                                        "process-status",
                                        "task-category-attributes",
                                        "task-status",
                                        "job-category-attributes",
                                        "job-status",
                                      ].includes(activeSection)
                                    ) ?
                                      "active"
                                    : ""
                                  }`}
                                  onClick={() => setOpenAttributes((v) => !v)}
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    width: "100%",
                                  }}
                                >
                                  Attributes
                                  <span
                                    className={`submenu-caret ${openAttributes ? "open" : ""}`}
                                    aria-hidden="true"
                                    style={{ marginLeft: 8 }}
                                  >
                                    ▾
                                  </span>
                                </button>
                                {openAttributes && (
                                  <ul
                                    className="nav-sublist"
                                    style={{ marginTop: 4, marginLeft: 16 }}
                                  >
                                    <li className="nav-subitem">
                                      <SidebarSectionLink
                                        section="task-category-attributes"
                                        className={`nav-subbutton ${
                                          (
                                            activeSection ===
                                            "task-category-attributes"
                                          ) ?
                                            "active"
                                          : ""
                                        }`}
                                        onNavigate={setActiveSection}
                                      >
                                        Task Category attributes
                                      </SidebarSectionLink>
                                    </li>
                                    <li className="nav-subitem">
                                      <SidebarSectionLink
                                        section="process-category-attributes"
                                        className={`nav-subbutton ${
                                          (
                                            activeSection ===
                                            "process-category-attributes"
                                          ) ?
                                            "active"
                                          : ""
                                        }`}
                                        onNavigate={setActiveSection}
                                      >
                                        Process Category attributes
                                      </SidebarSectionLink>
                                    </li>
                                    <li className="nav-subitem">
                                      <SidebarSectionLink
                                        section="task-status"
                                        className={`nav-subbutton ${
                                          activeSection === "task-status" ?
                                            "active"
                                          : ""
                                        }`}
                                        onNavigate={setActiveSection}
                                      >
                                        Task Status
                                      </SidebarSectionLink>
                                    </li>
                                    <li className="nav-subitem">
                                      <SidebarSectionLink
                                        section="process-status"
                                        className={`nav-subbutton ${
                                          activeSection === "process-status" ?
                                            "active"
                                          : ""
                                        }`}
                                        onNavigate={setActiveSection}
                                      >
                                        Process Status
                                      </SidebarSectionLink>
                                    </li>
                                    <li className="nav-subitem">
                                      <SidebarSectionLink
                                        section="job-category-attributes"
                                        className={`nav-subbutton ${
                                          (
                                            activeSection ===
                                            "job-category-attributes"
                                          ) ?
                                            "active"
                                          : ""
                                        }`}
                                        onNavigate={setActiveSection}
                                      >
                                        Job Category attributes
                                      </SidebarSectionLink>
                                    </li>
                                    <li className="nav-subitem">
                                      <SidebarSectionLink
                                        section="job-status"
                                        className={`nav-subbutton ${
                                          activeSection === "job-status" ?
                                            "active"
                                          : ""
                                        }`}
                                        onNavigate={setActiveSection}
                                      >
                                        Job Status
                                      </SidebarSectionLink>
                                    </li>
                                  </ul>
                                )}
                              </li>
                              <li className="nav-subitem">
                                <button
                                  className={`nav-subbutton ${
                                    (
                                      ["countries", "cities"].includes(
                                        activeSection,
                                      )
                                    ) ?
                                      "active"
                                    : ""
                                  }`}
                                  onClick={() => setOpenSettings((v) => !v)}
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    width: "100%",
                                  }}
                                >
                                  Settings
                                  <span
                                    className={`submenu-caret ${openSettings ? "open" : ""}`}
                                    aria-hidden="true"
                                    style={{ marginLeft: 8 }}
                                  >
                                    ▾
                                  </span>
                                </button>
                                {openSettings && (
                                  <ul
                                    className="nav-sublist"
                                    style={{ marginTop: 4, marginLeft: 16 }}
                                  >
                                    <li className="nav-subitem">
                                      <SidebarSectionLink
                                        section="countries"
                                        className={`nav-subbutton ${
                                          activeSection === "countries" ?
                                            "active"
                                          : ""
                                        }`}
                                        onNavigate={setActiveSection}
                                      >
                                        Countries
                                      </SidebarSectionLink>
                                    </li>
                                    <li className="nav-subitem">
                                      <SidebarSectionLink
                                        section="cities"
                                        className={`nav-subbutton ${
                                          activeSection === "cities" ? "active"
                                          : ""
                                        }`}
                                        onNavigate={setActiveSection}
                                      >
                                        Cities
                                      </SidebarSectionLink>
                                    </li>
                                  </ul>
                                )}
                              </li>
                            </>
                          )}
                        </ul>
                      )}
                    </>
                  );
                })()
              : item.id === "company" ?
                <>
                  <button
                    className={`nav-button ${
                      (
                        [
                          "companies",
                          "buildings",
                          "building-detail",
                          "building-floors",
                          "rooms",
                          "company-management",
                          "organization-management",
                        ].includes(activeSection)
                      ) ?
                        "active"
                      : ""
                    }`}
                    onClick={() => toggleGroup(setOpenCompany)}
                    title={collapsed ? item.label : ""}
                  >
                    <span className="nav-icon" aria-hidden="true">
                      {icons[item.id]}
                    </span>
                    {!collapsed && (
                      <span
                        className="nav-label"
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          width: "100%",
                          alignItems: "center",
                        }}
                      >
                        {item.label}
                        <span
                          className={`submenu-caret ${
                            openCompany ? "open" : ""
                          }`}
                          aria-hidden="true"
                        >
                          ▾
                        </span>
                      </span>
                    )}
                  </button>
                  {!collapsed && openCompany && (
                    <ul
                      className="nav-sublist"
                      style={{ marginTop: 4, marginLeft: 36 }}
                    >
                      <li className="nav-subitem">
                        <SidebarSectionLink
                          section="companies"
                          className={`nav-subbutton ${
                            activeSection === "companies" ? "active" : ""
                          }`}
                          onNavigate={setActiveSection}
                        >
                          Companies
                        </SidebarSectionLink>
                      </li>
                      <li className="nav-subitem">
                        <SidebarSectionLink
                          section="buildings"
                          className={`nav-subbutton ${
                            (
                              [
                                "buildings",
                                "building-create",
                                "building-detail",
                                "building-edit",
                              ].includes(activeSection)
                            ) ?
                              "active"
                            : ""
                          }`}
                          onNavigate={setActiveSection}
                        >
                          Buildings
                        </SidebarSectionLink>
                      </li>
                      <li className="nav-subitem">
                        <SidebarSectionLink
                          section="building-floors"
                          className={`nav-subbutton ${
                            activeSection === "building-floors" ? "active" : ""
                          }`}
                          onNavigate={setActiveSection}
                        >
                          Floors
                        </SidebarSectionLink>
                      </li>
                      <li className="nav-subitem">
                        <SidebarSectionLink
                          section="rooms"
                          className={`nav-subbutton ${
                            activeSection === "rooms" ? "active" : ""
                          }`}
                          onNavigate={setActiveSection}
                        >
                          Rooms
                        </SidebarSectionLink>
                      </li>
                    </ul>
                  )}
                </>
              : item.id === "activities" ?
                <>
                  <button
                    className={`nav-button ${
                      (
                        [
                          "process-management",
                          "task-management",
                          "bpmn-viewer",
                          "process-builder",
                          "process-usecase",
                          "process-user-stories",
                          "process-prototype",
                        ].includes(activeSection)
                      ) ?
                        "active"
                      : ""
                    }`}
                    onClick={() => toggleGroup(setOpenActivities)}
                    title={collapsed ? item.label : ""}
                  >
                    <span className="nav-icon" aria-hidden="true">
                      {icons[item.id]}
                    </span>
                    {!collapsed && (
                      <span
                        className="nav-label"
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          width: "100%",
                          alignItems: "center",
                        }}
                      >
                        {item.label}
                        <span
                          className={`submenu-caret ${
                            openActivities ? "open" : ""
                          }`}
                          aria-hidden="true"
                        >
                          ▾
                        </span>
                      </span>
                    )}
                  </button>
                  {!collapsed && openActivities && (
                    <ul
                      className="nav-sublist"
                      style={{ marginTop: 4, marginLeft: 36 }}
                    >
                      <li className="nav-subitem">
                        <SidebarSectionLink
                          section="process-management"
                          className={`nav-subbutton ${
                            activeSection === "process-management" ? "active"
                            : ""
                          }`}
                          onNavigate={setActiveSection}
                        >
                          Processes
                        </SidebarSectionLink>
                      </li>
                      <li className="nav-subitem">
                        <SidebarSectionLink
                          section="task-management"
                          className={`nav-subbutton ${
                            activeSection === "task-management" ? "active" : ""
                          }`}
                          onNavigate={setActiveSection}
                        >
                          Tasks
                        </SidebarSectionLink>
                      </li>
                      <li className="nav-subitem">
                        <SidebarSectionLink
                          section="bpmn-viewer"
                          className={`nav-subbutton ${
                            activeSection === "bpmn-viewer" ? "active" : ""
                          }`}
                          onNavigate={setActiveSection}
                        >
                          BPMN
                        </SidebarSectionLink>
                      </li>
                      <li className="nav-subitem">
                        <SidebarSectionLink
                          section="process-builder"
                          className={`nav-subbutton ${
                            activeSection === "process-builder" ? "active" : ""
                          }`}
                          onNavigate={setActiveSection}
                        >
                          Process Builder
                        </SidebarSectionLink>
                      </li>
                      <li className="nav-subitem">
                        <SidebarSectionLink
                          section="process-user-stories"
                          className={`nav-subbutton ${
                            (
                              [
                                "process-user-stories",
                                "process-usecase",
                                "process-prototype",
                              ].includes(activeSection)
                            ) ?
                              "active"
                            : ""
                          }`}
                          onNavigate={setActiveSection}
                        >
                          Process Studio
                        </SidebarSectionLink>
                      </li>
                    </ul>
                  )}
                </>
              : item.id === "organization" ?
                <>
                  <button
                    className={`nav-button ${
                      (
                        [
                          "job-management",
                          "function-management",
                          "people-management",
                          "functional-hierarchy",
                          "unassigned-entities",
                          // "c-level-library-new",
                          "c-level-org-chart",
                        ].includes(activeSection)
                      ) ?
                        "active"
                      : ""
                    }`}
                    onClick={() => toggleGroup(setOpenOrganization)}
                    title={collapsed ? item.label : ""}
                  >
                    <span className="nav-icon" aria-hidden="true">
                      {icons[item.id]}
                    </span>
                    {!collapsed && (
                      <span
                        className="nav-label"
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          width: "100%",
                          alignItems: "center",
                        }}
                      >
                        {item.label}
                        <span
                          className={`submenu-caret ${
                            openOrganization ? "open" : ""
                          }`}
                          aria-hidden="true"
                        >
                          ▾
                        </span>
                      </span>
                    )}
                  </button>
                  {!collapsed && openOrganization && (
                    <ul
                      className="nav-sublist"
                      style={{ marginTop: 4, marginLeft: 36 }}
                    >
                      <li className="nav-subitem">
                        <SidebarSectionLink
                          section="job-management"
                          className={`nav-subbutton ${
                            activeSection === "job-management" ? "active" : ""
                          }`}
                          onNavigate={setActiveSection}
                        >
                          Jobs
                        </SidebarSectionLink>
                      </li>
                      <li className="nav-subitem">
                        <SidebarSectionLink
                          section="job-workload"
                          className={`nav-subbutton ${
                            activeSection === "job-workload" ? "active" : ""
                          }`}
                          onNavigate={setActiveSection}
                        >
                          Job Workload
                        </SidebarSectionLink>
                      </li>
                      <li className="nav-subitem">
                        <SidebarSectionLink
                          section="function-management"
                          className={`nav-subbutton ${
                            activeSection === "function-management" ? "active"
                            : ""
                          }`}
                          onNavigate={setActiveSection}
                        >
                          Functions
                        </SidebarSectionLink>
                      </li>
                      <li className="nav-subitem">
                        <SidebarSectionLink
                          section="people-management"
                          className={`nav-subbutton ${
                            activeSection === "people-management" ? "active" : (
                              ""
                            )
                          }`}
                          onNavigate={setActiveSection}
                        >
                          People
                        </SidebarSectionLink>
                      </li>
                      <li className="nav-subitem">
                        <SidebarSectionLink
                          section="unassigned-entities"
                          className={`nav-subbutton ${
                            activeSection === "unassigned-entities" ? "active"
                            : ""
                          }`}
                          onNavigate={setActiveSection}
                        >
                          Unassigned Entities
                        </SidebarSectionLink>
                      </li>
                      <li className="nav-subitem">
                        <SidebarSectionLink
                          section="functional-hierarchy"
                          className={`nav-subbutton ${
                            activeSection === "functional-hierarchy" ? "active"
                            : ""
                          }`}
                          onNavigate={setActiveSection}
                        >
                          Functional Hierarchy
                        </SidebarSectionLink>
                      </li>
                      {/**
                        * C-Level (New) temporarily hidden
                        * <li className="nav-subitem">
                        *   <SidebarSectionLink
                        *     section="c-level-library-new"
                        *     className={`nav-subbutton ${
                        *       activeSection === "c-level-library-new" ? "active" : ""
                        *     }`}
                        *     onNavigate={setActiveSection}
                        *   >
                        *     C-Level (New)
                        *   </SidebarSectionLink>
                        * </li>
                        */}
                      <li className="nav-subitem">
                        <SidebarSectionLink
                          section="c-level-org-chart"
                          className={`nav-subbutton ${
                            activeSection === "c-level-org-chart" ? "active" : (
                              ""
                            )
                          }`}
                          onNavigate={setActiveSection}
                        >
                          C-Level Org Chart
                        </SidebarSectionLink>
                      </li>
                    </ul>
                  )}
                </>
              : item.id === "ai-agents" ?
                <>
                  <button
                    className={`nav-button ${
                      (
                        [
                          "what-if-analysis",
                          "process-optimization",
                          "process-optimization-results",
                          "process-optimization-copy",
                          "substitution-analyst",
                          "capi-paei-analysis",
                          // "process-creation",
                          // "process-visualization",
                        ].includes(activeSection)
                      ) ?
                        "active"
                      : ""
                    }`}
                    onClick={() => toggleGroup(setOpenAI)}
                    title={collapsed ? item.label : ""}
                  >
                    <span className="nav-icon" aria-hidden="true">
                      {icons[item.id]}
                    </span>
                    {!collapsed && (
                      <span
                        className="nav-label"
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          width: "100%",
                          alignItems: "center",
                        }}
                      >
                        {item.label}
                        <span
                          className={`submenu-caret ${openAI ? "open" : ""}`}
                          aria-hidden="true"
                        >
                          ▾
                        </span>
                      </span>
                    )}
                  </button>
                  {!collapsed && openAI && (
                    <ul
                      className="nav-sublist"
                      style={{ marginTop: 4, marginLeft: 36 }}
                    >
                      <li className="nav-subitem">
                        <SidebarSectionLink
                          section="what-if-analysis"
                          className={`nav-subbutton ${
                            activeSection === "what-if-analysis" ? "active" : ""
                          }`}
                          onNavigate={setActiveSection}
                        >
                          What-if Analysis
                        </SidebarSectionLink>
                      </li>
                      {/**
                       * Process Creation (Prototype) temporarily hidden
                       * <li className="nav-subitem">
                       *   <button
                       *     className={`nav-subbutton ${
                       *       activeSection === "process-creation" ? "active" : ""
                       *     }`}
                       *     onClick={() => setActiveSection("process-creation")}
                       *   >
                       *     Process Creation (Prototype)
                       *   </button>
                       * </li>
                       */}
                      <li className="nav-subitem">
                        <SidebarSectionLink
                          section="process-optimization"
                          className={`nav-subbutton ${
                            (
                              activeSection === "process-optimization" ||
                              activeSection === "process-optimization-results"
                            ) ?
                              "active"
                            : ""
                          }`}
                          onNavigate={setActiveSection}
                        >
                          Process Optimization
                        </SidebarSectionLink>
                      </li>
                      <li className="nav-subitem">
                        <SidebarSectionLink
                          section="substitution-analyst"
                          className={`nav-subbutton ${
                            activeSection === "substitution-analyst" ?
                              "active"
                            : ""
                          }`}
                          onNavigate={setActiveSection}
                        >
                          Substitution Analyst
                        </SidebarSectionLink>
                      </li>
                      <li className="nav-subitem">
                        <SidebarSectionLink
                          section="capi-paei-analysis"
                          className={`nav-subbutton ${
                            activeSection === "capi-paei-analysis" ? "active" : ""
                          }`}
                          onNavigate={setActiveSection}
                        >
                          CAPI &amp; PAEI Analysis
                        </SidebarSectionLink>
                      </li>
                      {/**
                       * Process Visualization (Prototype) temporarily hidden
                       * <li className="nav-subitem">
                       *   <button
                       *     className={`nav-subbutton ${
                       *       activeSection === "process-visualization" ? "active"
                       *       : ""
                       *     }`}
                       *     onClick={() =>
                       *       setActiveSection("process-visualization")
                       *     }
                       *   >
                       *     Process Visualization (Prototype)
                       *   </button>
                       * </li>
                       */}
                    </ul>
                  )}
                </>
              : item.id === "help" ?
                <>
                  <button
                    className={`nav-button ${
                      ["help-docs", "help-topic"].includes(activeSection) ?
                        "active"
                      : ""
                    }`}
                    onClick={() => toggleGroup(setOpenHelp)}
                    title={collapsed ? item.label : ""}
                  >
                    <span className="nav-icon" aria-hidden="true">
                      {icons[item.id]}
                    </span>
                    {!collapsed && (
                      <span
                        className="nav-label"
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          width: "100%",
                          alignItems: "center",
                        }}
                      >
                        {item.label}
                        <span
                          className={`submenu-caret ${openHelp ? "open" : ""}`}
                          aria-hidden="true"
                        >
                          ▾
                        </span>
                      </span>
                    )}
                  </button>
                  {!collapsed && openHelp && (
                    <ul
                      className="nav-sublist"
                      style={{ marginTop: 4, marginLeft: 36 }}
                    >
                      <li className="nav-subitem">
                        <SidebarSectionLink
                          section="help-docs"
                          className={`nav-subbutton ${
                            ["help-docs", "help-topic"].includes(activeSection) ?
                              "active"
                            : ""
                          }`}
                          onNavigate={setActiveSection}
                        >
                          User Manual
                        </SidebarSectionLink>
                      </li>
                    </ul>
                  )}
                </>
              : <SidebarSectionLink
                  section={item.id}
                  className={`nav-button ${
                    activeSection === item.id ? "active" : ""
                  }`}
                  onNavigate={setActiveSection}
                  title={collapsed ? item.label : ""}
                >
                  <span className="nav-icon" aria-hidden="true">
                    {icons[item.id]}
                  </span>
                  {!collapsed && (
                    <span className="nav-label">{item.label}</span>
                  )}
                </SidebarSectionLink>
              }
            </li>
          ))}
        </ul>
      </nav>
    </aside>
  );
};

export default Sidebar;
